/* ═══════════════════════════════════════════════════════════════
   InvoiceService — Invoice CRUD via EC2 API.

   Tracking-only model: we store the invoice + line items + lifecycle
   dates + payment amounts.  No payment processor, no bank rails — that
   stays in QuickBooks or wherever the firm does actual money movement.

   Invoice numbering: simple incrementing pattern INV-YYYY-NNNN (per
   firm).  We compute the next number client-side from the existing
   list — fine until two PMs hit "Create" within the same render tick;
   the rare collision is recoverable (server has no UNIQUE constraint
   on invoice_number, so the worst case is a duplicate string the user
   can edit).
   ═══════════════════════════════════════════════════════════════ */

import type { Invoice, InvoiceLineItem, HourEntry } from '../../shared/types';
import * as cache from './cloudCache';
import type { TableOpts } from './cloudCache';
import * as cloudApi from './apiClient';
import { getCurrentFirmId } from './firmContext';
import { log as logActivity } from './activityLogger';

const invoiceOpts: TableOpts = {
  jsonFields: ['lineItems'],
  columnMap: { line_items: 'line_items_json' },
};

/** Compute the next invoice number for a firm based on existing rows.
 *  Pattern: INV-YYYY-NNNN where NNNN is the highest existing N+1 for the
 *  current year, or 0001 when no invoices exist this year. */
function nextInvoiceNumber(existing: Invoice[]): string {
  const year = new Date().getFullYear();
  const prefix = `INV-${year}-`;
  let max = 0;
  for (const inv of existing) {
    if (!inv.invoiceNumber || !inv.invoiceNumber.startsWith(prefix)) continue;
    const tail = inv.invoiceNumber.slice(prefix.length);
    const n = parseInt(tail, 10);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${prefix}${String(max + 1).padStart(4, '0')}`;
}

// Server-side snake_case row -> camelCase Invoice, matching cache.save's
// contract (rowToCamel with invoiceOpts). Used for the first-class
// lifecycle actions below, which bypass cache.save (RETURNING * comes
// back snake_case, with the JSONB `line_items_json` column pg already
// parses into a JS array). Tranche 0 item 2.
function toCamelInvoice(row: Record<string, any>): Invoice {
  const camel: Record<string, any> = {};
  for (const [k, v] of Object.entries(row)) {
    if (k === 'line_items_json') {
      camel.lineItems = v;
      continue;
    }
    camel[k.replace(/_([a-z])/g, (_, c) => c.toUpperCase())] = v;
  }
  return camel as Invoice;
}

export const InvoiceService = {
  list: async (projectId?: string): Promise<Invoice[]> => {
    if (projectId) return cache.query<Invoice>('invoices', { projectId }, invoiceOpts);
    return cache.list<Invoice>('invoices', invoiceOpts);
  },

  get: async (id: string): Promise<Invoice | null> => {
    const all = await InvoiceService.list();
    return all.find((i) => i.id === id) || null;
  },

  /** Tranche 0 item 2/7: a status change AWAY from 'draft' now routes
   *  through the dedicated invoice_send / invoice_mark_paid / invoice_void
   *  server action (permission-gated per-transition, CALLER stamped
   *  server-side) — the generic save path rejects these transitions now.
   *  Draft-only edits (line items, notes, etc.) are unaffected and stay on
   *  the generic path. Item 5: after any successful save, any 'time'-
   *  sourced hour_entries referenced by the current line items are
   *  stamped with this invoice's id (idempotent — only claims currently-
   *  unbilled rows), which is what actually prevents double-billing. */
  save: async (inv: Invoice): Promise<Invoice> => {
    const isNew = !inv.id;
    const prev = !isNew ? await InvoiceService.get(inv.id) : null;
    const prevStatus = prev?.status ?? null;

    let saved: Invoice;
    if (!isNew && prevStatus && inv.status !== prevStatus && inv.status !== 'draft') {
      if (inv.status === 'sent') {
        saved = toCamelInvoice(await cloudApi.invoiceSend(inv.id));
      } else if (inv.status === 'void') {
        saved = toCamelInvoice(await cloudApi.invoiceVoid(inv.id));
      } else if (inv.status === 'paid' || inv.status === 'partial') {
        const amt = Math.max(0, (inv.amountPaid || 0) - (prev?.amountPaid || 0));
        saved = toCamelInvoice(await cloudApi.invoiceMarkPaid(inv.id, amt));
      } else {
        // Unrecognized target status — fall through to a plain save
        // rather than silently dropping the edit; the server's own
        // status guard is the real backstop either way.
        saved = await cache.save('invoices', inv, invoiceOpts);
      }
      cache.invalidate('invoices');
    } else {
      saved = await cache.save('invoices', inv, invoiceOpts);
    }

    // Item 5 — stamp pulled hour_entries as billed.
    const timeEntryIds = (inv.lineItems || [])
      .filter((li) => li.sourceKind === 'time')
      .flatMap((li) => li.sourceIds || []);
    if (timeEntryIds.length > 0) {
      try {
        await cloudApi.invoiceStampEntries(saved.id, timeEntryIds);
        cache.invalidate('hour_entries');
      } catch (err: any) {
        // Non-fatal — the invoice itself saved fine; surface via the same
        // console channel other best-effort cache calls use rather than
        // failing the whole save over a billing-hygiene side effect.
        console.error('invoice_stamp_entries failed:', err?.message || err);
      }
    }

    // Differentiate status changes (sent / paid) from plain saves so the
    // activity log surfaces business-meaningful events not just edits.
    let action = isNew ? 'invoice.create' : 'invoice.update';
    if (!isNew && prevStatus !== saved.status) {
      if (saved.status === 'sent') action = 'invoice.send';
      else if (saved.status === 'paid') action = 'invoice.paid';
      else if (saved.status === 'void') action = 'invoice.void';
    }
    logActivity({
      action,
      entityType: 'invoice',
      entityId: saved.id,
      metadata: {
        invoiceNumber: saved.invoiceNumber,
        status: saved.status,
        total: saved.total,
        projectId: saved.projectId,
      },
    });
    return saved;
  },

  delete: async (id: string): Promise<void> => {
    await cache.remove('invoices', id);
    cache.invalidate('hour_entries'); // server-side delete unstamps pulled hour_entries
    logActivity({ action: 'invoice.delete', entityType: 'invoice', entityId: id });
  },

  /** Archive or restore — a THREE-key partial update on purpose: the server
   *  reads an `archived_at` flip of three keys or fewer as an archive toggle
   *  and gates it on `invoices.archive`. Mirrors ModuleService.setArchived. */
  setArchived: async (id: string, archived: boolean, userName?: string): Promise<void> => {
    const firmId = getCurrentFirmId();
    await cloudApi.dbUpdate(
      'invoices',
      {
        archived_at: archived ? new Date().toISOString() : null,
        archived_by: archived ? userName || null : null,
      },
      [
        { col: 'firm_id', op: '=', val: firmId },
        { col: 'id', op: '=', val: id },
      ]
    );
    cache.invalidate('invoices');
    logActivity({
      action: archived ? 'invoice.archive' : 'invoice.restore',
      entityType: 'invoice',
      entityId: id,
    });
  },

  /** Allocate the next invoice number for the current firm. */
  nextInvoiceNumber: async (): Promise<string> => {
    const all = await InvoiceService.list();
    return nextInvoiceNumber(all);
  },

  /** Build line items from a list of hour entries.  Groups by project +
   *  activity code, sums hours, and emits one line item per group with
   *  source_kind='time' + source_ids = the consumed entry IDs.  Caller
   *  supplies a rate-resolver that returns the $/hr for each entry
   *  (typically billing-role default rate or the team-member override). */
  lineItemsFromTimeEntries: (
    entries: HourEntry[],
    rateFor: (entry: HourEntry) => number,
    descFor: (entry: HourEntry) => string
  ): InvoiceLineItem[] => {
    // Group by descFor's output so identical rate × description rows merge.
    const groups = new Map<
      string,
      { description: string; rate: number; hours: number; ids: string[] }
    >();
    for (const e of entries) {
      const rate = rateFor(e) || 0;
      const desc = descFor(e);
      const key = `${desc}|${rate}`;
      const g = groups.get(key) || { description: desc, rate, hours: 0, ids: [] };
      g.hours += Number(e.hours) || 0;
      g.ids.push(e.id);
      groups.set(key, g);
    }
    return Array.from(groups.values()).map((g) => ({
      description: g.description,
      // Schema column is `hours` (numeric); we mirror to `quantity` for
      // non-time line items in the editor.
      hours: Math.round(g.hours * 100) / 100,
      quantity: Math.round(g.hours * 100) / 100,
      rate: g.rate,
      amount: Math.round(g.hours * g.rate * 100) / 100,
      sourceKind: 'time' as const,
      sourceIds: g.ids,
    }));
  },

  /** Recompute subtotal + tax + total from line items. */
  recompute: (inv: Invoice): Invoice => {
    const subtotal = (inv.lineItems || []).reduce((s, li) => s + (li.amount || 0), 0);
    const taxRate = (inv as any).taxRate ?? 0;
    const tax = Math.round(subtotal * (taxRate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax) * 100) / 100;
    return { ...inv, subtotal, tax, total };
  },

  /** AR aging buckets across all firm invoices. */
  ageingBuckets: (invoices: Invoice[]) => {
    const today = new Date();
    const out = { current: 0, d30: 0, d60: 0, d90: 0 };
    for (const inv of invoices) {
      const status = (inv.status || '') as string;
      if (!status || status === 'draft' || status === 'paid' || status === 'void') continue;
      const issued = (inv as any).issueDate || (inv as any).date;
      if (!issued) continue;
      const days = Math.floor((today.getTime() - new Date(issued).getTime()) / 86400000);
      const open = (inv.total || 0) - ((inv as any).amountPaid || 0);
      if (open <= 0) continue;
      if (days <= 30) out.current += open;
      else if (days <= 60) out.d30 += open;
      else if (days <= 90) out.d60 += open;
      else out.d90 += open;
    }
    return out;
  },
};
